import React from "react";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";

export type BubblePoint = {
    x: number;
    y: number;
    r: number;
    color: string;
    label: string;
    tip: string;
};

type Props = {
    points: BubblePoint[];
    xLabel?: string;
    yLabel?: string;
    xMax?: number;
    yMax?: number;
    yMin?: number;
    formatX: (value: number) => string;
    formatY?: (value: number) => string;
};

export const BubbleMatrix: React.FC<Props> = ({
    points,
    xLabel = "Volume",
    yLabel = "Margin",
    xMax,
    yMax,
    yMin = 0,
    formatX,
    formatY = (value) => `${value}%`,
}) => {
    if (points.length === 0) return null;

    const W = 640;
    const H = 380;
    const P = { t: 20, r: 24, b: 46, l: 52 };
    const iw = W - P.l - P.r;
    const ih = H - P.t - P.b;
    const xmax = xMax !== undefined ? xMax : Math.max(...points.map((point) => point.x)) * 1.12 || 1;
    const ymax = yMax !== undefined ? yMax : Math.max(...points.map((point) => point.y)) * 1.1 || 1;
    const x = (value: number) => P.l + (value / xmax) * iw;
    const y = (value: number) => P.t + ih - ((value - yMin) / (ymax - yMin || 1)) * ih;
    const gridSteps = [0, 1, 2, 3, 4];

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            preserveAspectRatio="xMidYMid meet"
            style={{ display: "block", overflow: "visible" }}
        >
            {gridSteps.map((step) => {
                const gy = P.t + (ih / 4) * step;
                const yValue = ymax - ((ymax - yMin) / 4) * step;
                const gx = P.l + (iw / 4) * step;
                const xValue = (xmax / 4) * step;
                return (
                    <g key={step}>
                        <line
                            x1={P.l}
                            y1={gy.toFixed(1)}
                            x2={W - P.r}
                            y2={gy.toFixed(1)}
                            stroke={CHART_COLORS.grid}
                            strokeWidth={1}
                        />
                        <text
                            x={P.l - 8}
                            y={(gy + 3).toFixed(1)}
                            textAnchor="end"
                            fill={CHART_COLORS.axis}
                            fontSize={10.5}
                        >
                            {formatY(Math.round(yValue * 10) / 10)}
                        </text>
                        <text
                            x={gx.toFixed(1)}
                            y={H - 24}
                            textAnchor="middle"
                            fill={CHART_COLORS.axis}
                            fontSize={10.5}
                        >
                            {formatX(xValue)}
                        </text>
                    </g>
                );
            })}
            {points.map((point) => (
                <g key={point.label} style={{ cursor: "pointer" }} data-tip={point.tip}>
                    <circle
                        cx={x(point.x).toFixed(1)}
                        cy={y(point.y).toFixed(1)}
                        r={point.r}
                        fill={point.color}
                        fillOpacity={0.2}
                        stroke={point.color}
                        strokeWidth={1.5}
                    />
                    <circle cx={x(point.x).toFixed(1)} cy={y(point.y).toFixed(1)} r={3} fill={point.color} />
                    <text
                        x={x(point.x).toFixed(1)}
                        y={(y(point.y) - point.r - 6).toFixed(1)}
                        textAnchor="middle"
                        fill="#CBD5E1"
                        fontSize={10.5}
                        fontWeight={600}
                    >
                        {point.label}
                    </text>
                </g>
            ))}
            <text
                x={P.l + iw / 2}
                y={H - 4}
                textAnchor="middle"
                fill={CHART_COLORS.axisStrong}
                fontSize={11}
                fontWeight={600}
            >
                {xLabel} →
            </text>
            <text
                x={14}
                y={P.t + ih / 2}
                textAnchor="middle"
                fill={CHART_COLORS.axisStrong}
                fontSize={11}
                fontWeight={600}
                transform={`rotate(-90 14 ${P.t + ih / 2})`}
            >
                {yLabel} →
            </text>
        </svg>
    );
};
